/**
 * Maintenance history stage — structure-scoped, nested in the site dashboard.
 *
 * Rendered as a child route of SiteDashboardLayout, inside the shared
 * dashboard shell (which supplies AppNav + TitleBar). Lists the site's raw
 * maintenance log entries and lets permitted users add new entries or resolve
 * open ones.
 *
 * Contains:
 * - MaintenanceHistoryView — route child component (default export)
 */
import AddIcon from '@mui/icons-material/Add';
import { Box, Button } from '@mui/material';
import { List } from 'immutable';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useUser } from '@sophocles/app';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import { fetchAllRawSiteMaintenanceLogsForSite } from '@sophocles/entities/SiteMaintenanceLog';
import { userPermCheck } from '@sophocles/utils/userPermissionUtils';
import AddMaintenanceLogDialog from '@sophocles/views/sites/components/MaintenanceLogCard/AddMaintenanceLogDialog';
import ResolveMaintenanceLogDialog from '@sophocles/views/sites/components/MaintenanceLogCard/ResolveMaintenanceLogDialog';
import { useSiteDashboardLayoutContext } from '@sophocles/views/sites/SiteDashboardLayout';

import MaintenanceHistoryGrid from './components/MaintenanceHistoryGrid';

function MaintenanceHistoryView() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useUser();
  const { selectedStructure, loading: siteLoading } = useSiteDashboardLayoutContext();

  const [data, setData] = useState(List());
  const [selectedLog, setSelectedLog] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const canAddEntries = userPermCheck(user, 'pliny.add_sitemaintenancelog');
  const canEditEntries = userPermCheck(user, 'pliny.change_sitemaintenancelog');

  const startLoading = useCallback(() => setLoading(true), []);
  const endLoading = useCallback(() => setLoading(false), []);

  const fetchData = useCallback(() => {
    startLoading();
    return dispatch(fetchAllRawSiteMaintenanceLogsForSite(selectedStructure.get('id', '0'))).then(res => {
      const next = res.map(entry =>
        entry
          .update('time_entered', v => (v ? moment(v) : null))
          .update('date_reported', v => (v ? moment(v) : null))
          .update('date_resolved', v => (v ? moment(v) : null)),
      );
      setData(next);
      endLoading();
    });
  }, [dispatch, selectedStructure, startLoading, endLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleResolve = useCallback(log => setSelectedLog(log), []);
  const handleDeselectLog = useCallback(() => setSelectedLog(null), []);
  const openNewEntry = useCallback(() => setNewOpen(true), []);
  const closeNewEntry = useCallback(() => setNewOpen(false), []);

  if (siteLoading) {
    return <LoadingScreen />;
  }

  return (
    <Box sx={{ pb: 2, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {canAddEntries ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', py: 1 }}>
          <Button variant="contained" color="primary" onClick={openNewEntry} startIcon={<AddIcon />}>
            {t('actions.add')}
          </Button>
        </Box>
      ) : null}

      {loading && <LoadingScreen />}

      <MaintenanceHistoryGrid data={data} loading={loading} canEditEntries={canEditEntries} onResolve={handleResolve} />

      {selectedLog ? (
        <ResolveMaintenanceLogDialog
          onResult={fetchData}
          open={selectedLog != null}
          closeDialog={handleDeselectLog}
          user={user}
          log={selectedLog}
          startLoading={startLoading}
          endLoading={endLoading}
        />
      ) : null}
      {newOpen ? (
        <AddMaintenanceLogDialog
          onResult={fetchData}
          open={newOpen}
          closeDialog={closeNewEntry}
          user={user}
          site={selectedStructure}
          startLoading={startLoading}
          endLoading={endLoading}
        />
      ) : null}
    </Box>
  );
}

export default MaintenanceHistoryView;
